import { Link } from "@tanstack/react-router";
import { type ReactNode, useId } from "react";
import { NewTab } from "~/components/inside/new-tab";
import { getCtaConfig } from "~/lib/constants/navbar";
import * as m from "~/paraglide/messages.js";

interface InnerCtaProps {
  lede: string;
  note?: string;
  /** Overrides booking a call, which is the default on every inner page. */
  primary?: { href: string; label: string };
  secondary?: { label: string; to: string };
  /** A node rather than a string: some pages break the heading in two. */
  title: ReactNode;
}

/**
 * The panel that ends an inner page.
 *
 * Deliberately not a generalisation of the home page's ClosingPanel: that one
 * is wired to the home messages, carries the brand mark, offers a second email
 * action and sits between two decorative squares. The two share the
 * `.closing-panel` base and nothing else.
 */
export function InnerCta({
  lede,
  note,
  primary,
  secondary,
  title,
}: Readonly<InnerCtaProps>) {
  const titleId = useId();
  // The booking URL is shared with the header, but not its label: the bar says
  // "Let's talk", the close asks for the call itself.
  const action = primary ?? {
    href: getCtaConfig().href,
    label: m.inner_cta_call(),
  };

  return (
    <section aria-labelledby={titleId} className="inner-cta wrap">
      <div className="closing-panel">
        <h2 id={titleId}>{title}</h2>
        <p>{lede}</p>

        <div className="actions">
          <a
            className="button button-solid"
            href={action.href}
            rel="noopener"
            target="_blank"
          >
            {action.label}
            <NewTab />
          </a>
          {secondary ? (
            <Link className="button button-outline" to={secondary.to}>
              {secondary.label}
            </Link>
          ) : null}
        </div>

        {note ? <span className="small-note">{note}</span> : null}
      </div>
    </section>
  );
}
